import { Notification } from '@thanx/uikit/notification'
import { Text } from '@thanx/uikit/text'
import { getLocations } from 'actions/location'
import {
  useUpdateLocationConfigsMutation,
  useUpdateRecoveryConfigMutation,
} from 'api/recoveryAi'
import type { UpdateLocationConfigsBody } from 'api/recoveryAi.types'
import Spinner from 'components/Spinner'
import useDispatch from 'hooks/useDispatch'
import { buildTranslate } from 'locales'
import React, { useCallback, useEffect } from 'react'
import LocationModePicker, {
  LocationMode,
} from 'scenes/RecoveryAI/components/LocationModePicker'
import { useStyletron } from 'styletron-react'
import { useAbility } from 'utilities/ability'
import { WIZARD_CONTENT_MAX_WIDTH } from '../constants'
import { useWizardState } from '../WizardContext'
import { useWizardLocations } from './useWizardLocations'

const t = buildTranslate('recovery_ai.wizard.locations')
// The picker labels + save-error copy are shared verbatim with the Settings pane
// (AI-31); reuse those keys rather than duplicating them under wizard.locations.
const tShared = buildTranslate('recovery_ai.settings.active_locations')

// Em-dash for an absent total count M — never a literal 0 ("All 0 locations"
// would falsely read as "runs nowhere"). Mirrors ActiveLocationsPane / Landing.
const EM_DASH = '—'

/**
 * Onboarding wizard Locations stage (AI-161, revised AI-1130). Renders the
 * shared LocationModePicker wired to the useWizardLocations controller. Persists
 * the all-vs-subset selection via the dedicated PUT recovery/location_configs
 * (NOT the config PUT, which rejects location_configs — so this does NOT ride
 * submitStep/mapStepToConfig) — but AI-1130 removed this stage's own Save
 * button: the persist action is registered into the wizard's
 * continueActionRef (WizardContext) instead, and the wizard-chrome Continue
 * button (Wizard/index.tsx) runs it before advancing. Mirrors the
 * EscalationRouting step shell in every other respect.
 */
const LocationsStep = () => {
  const [css] = useStyletron()
  const dispatch = useDispatch()
  // Admin-only writes (the MerchantSettings CASL ability); view-only operators see the
  // active subset read-only with no write affordance (matches ActiveLocationsPane).
  const canManage = useAbility().can('manage', 'MerchantSettings')

  // AI-1173 follow-up: mirrors ActiveLocationsPane — the Redux-ORM Location
  // list isn't loaded anywhere near the wizard; useWizardLocations reads it to
  // append a blank-name row's street address. Intentionally unfiltered (no
  // coming_soon param) so its scope matches GET recovery/location_configs,
  // which has no coming_soon predicate. `dispatch` is referentially stable,
  // so this fires once per mount.
  useEffect(() => {
    dispatch(getLocations())
  }, [dispatch])
  const { continueActionRef } = useWizardState()
  const {
    locations,
    selectedLocations,
    mode,
    selectedIds,
    totalCount,
    onToggle,
    onRemove,
    onModeChange,
    isLoading,
    isError,
    enabledNow,
    newLocationDefault,
    locationsDirty,
    defaultDirty,
  } = useWizardLocations()

  // NOTE for the implementer: only `isError` is destructured from each mutation
  // result below — NOT `isLoading`. The original file also destructured
  // `isLoading` (as `isSavingLocations`/`isSavingConfig`) to drive the Save
  // button's spinner/disabled state; that button is gone, and nothing else in
  // this file reads those two bindings, so keeping them would be unused
  // variables (a tslint/tsc failure under this repo's config — verify with
  // `yarn tc` before considering this step done).
  const [updateLocationConfigs, { isError: isSaveErrorLocations, reset }] =
    useUpdateLocationConfigsMutation()
  const [updateRecoveryConfig, { isError: isConfigError, reset: resetConfig }] =
    useUpdateRecoveryConfigMutation()
  // Combined error across the two sequential writes (per-location PUT then the
  // config-flag PUT) so the banner below treats them as one op.
  const isSaveError = isSaveErrorLocations || isConfigError
  // Location write is sequenced first — a config-only error means the selection
  // persisted and only the auto-enroll flag failed, so name that write rather
  // than the generic "active locations failed" copy (mirrors ActiveLocationsPane).
  const isConfigOnlyError = isConfigError && !isSaveErrorLocations

  // Clear a stale error banner the moment the merchant edits again (mirrors the
  // pane), so a prior error never lingers over fresh changes.
  const clearSaveState = useCallback(() => {
    reset()
    resetConfig()
  }, [reset, resetConfig])

  const handleToggle = useCallback(
    (id: number) => {
      onToggle(id)
      clearSaveState()
    },
    [onToggle, clearSaveState]
  )
  const handleRemove = useCallback(
    (id: number) => {
      onRemove(id)
      clearSaveState()
    },
    [onRemove, clearSaveState]
  )
  const handleModeChange = useCallback(
    (next: LocationMode) => {
      onModeChange(next)
      clearSaveState()
    },
    [onModeChange, clearSaveState]
  )

  // AI-1130: this used to be a local Save button's onClick and swallowed its own
  // rejection (nowhere to route it). Now it's registered into continueActionRef
  // below and called by the wizard shell's Continue handler (Wizard/index.tsx),
  // which needs the rejection to propagate — a failed persist must NOT navigate.
  // isSaveError above (driven by the mutations' own state) still surfaces the
  // banner regardless of who calls this or whether they catch it.
  const handleSave = useCallback(async () => {
    // The dedicated PUT recovery/location_configs is the ONLY enable/disable
    // write path (the config PUT rejects location_configs). enabledNow is the
    // would-be-enabled set ("all" → every row; subset → the picked ids). raw-int
    // location_id at the boundary.
    const payload: UpdateLocationConfigsBody = {
      location_configs: locations.map(l => ({
        location_id: l.id,
        enabled: enabledNow.has(l.id),
      })),
    }
    // Sequential, primary-first (mirrors ActiveLocationsPane U3): the
    // per-location write, then reconcile the auto-enroll flag only when it
    // differs. Each guard avoids a no-op PUT; sequencing avoids racing the BE
    // find_or_create_by.
    if (locationsDirty) await updateLocationConfigs(payload).unwrap()
    if (defaultDirty)
      await updateRecoveryConfig({
        new_location_default_enabled: newLocationDefault,
      }).unwrap()
  }, [
    locations,
    enabledNow,
    locationsDirty,
    defaultDirty,
    newLocationDefault,
    updateLocationConfigs,
    updateRecoveryConfig,
  ])

  // Register/unregister this stage's persist action with the wizard shell. Runs
  // whenever handleSave's identity changes (i.e. any of its own deps above), and
  // unregisters on unmount so a later step's Continue never re-runs a stale
  // Locations-stage closure.
  //
  // Gated on canPersist (final review, AI-1130): the OLD Save button lived
  // inside `{canManage && (...)}` AND below the isLoading/isError early
  // returns below, so it was structurally unreachable in any of those three
  // states. A bare `continueActionRef.current = handleSave` here would widen
  // the reachable states — in particular, `defaultDirty` (useWizardLocations)
  // is derived PURELY from server state, not a user edit, and can be true on
  // first paint. Without this gate, a view-only operator, or a merchant who
  // clicks Continue while the location list GET is loading or has errored,
  // could silently fire the new_location_default_enabled PUT the UI otherwise
  // denies or has no data to justify.
  const canPersist = canManage && !isLoading && !isError
  useEffect(() => {
    continueActionRef.current = canPersist ? handleSave : null
    return () => {
      continueActionRef.current = null
    }
  }, [continueActionRef, handleSave, canPersist])

  // Block on the location-list GET (the source of truth for mode + selection).
  // Mirrors ActiveLocationsPane's loading/error branches — but with NO "locked"
  // branch: the wizard IS the pre-activation flow, so the stage must render the
  // picker, never a "locked" message. The error branch is checked before content
  // so a 404/500 surfaces an alert (now that #619 is merged, that's a real error,
  // not a gated-empty) rather than a silent empty picker. A genuinely empty list
  // falls through to the reused LocationMultiSelect empty-state.
  if (isLoading) {
    return (
      <div role="status" aria-label={tShared('loading')}>
        <Spinner isLoading size="4x" className="text-center margin-top-huge" />
      </div>
    )
  }
  if (isError) {
    return (
      <div className={`p-l ${css({ textAlign: 'center' })}`} role="alert">
        <Text.BodyText3 color="cayenne50">
          {tShared('fetch_error')}
        </Text.BodyText3>
      </div>
    )
  }

  // %{count} = the merchant's TOTAL location count M (not the enabled subset N);
  // an em-dash until metrics load — never a fabricated 0. PROVISIONAL count copy:
  // M is `merchant_visible` per metrics, and the exact "N of M" wording is
  // unreconciled pending AI-208 — do NOT lock it here. The call site picks the
  // _one/_other form (react-redux-i18n does not auto-pluralize).
  const allLabel = tShared(
    totalCount === 1 ? 'all_locations_one' : 'all_locations_other',
    { count: totalCount ?? EM_DASH }
  )

  const multiSelectLabels = {
    selectLocations: tShared('select_locations'),
    locationsSelected: tShared(
      selectedIds.length === 1
        ? 'locations_selected_one'
        : 'locations_selected_other',
      { count: selectedIds.length }
    ),
    noLocationsAvailable: tShared('no_locations_available'),
    searchLocations: tShared('search_locations'),
    noLocationsMatch: tShared('no_locations_match'),
  }

  return (
    <div
      className={css({ maxWidth: WIZARD_CONTENT_MAX_WIDTH, margin: '0 auto' })}
    >
      <div className="d-flex flex-column mb-xl">
        <Text.Header3 bold>{t('title')}</Text.Header3>
        <Text.BodyText3 color="grey70" className="mt-xs">
          {t('description')}
        </Text.BodyText3>
      </div>

      {isSaveError && (
        <div className="mb-m">
          <Notification kind="negative" size="full">
            {isConfigOnlyError
              ? tShared('save_error_config_only')
              : tShared('save_error')}
          </Notification>
        </div>
      )}

      <LocationModePicker
        mode={mode}
        onModeChange={handleModeChange}
        locations={locations}
        selectedIds={selectedIds}
        onToggle={handleToggle}
        onRemove={handleRemove}
        selectedLocations={selectedLocations}
        canManage={canManage}
        instanceId="wizard"
        labels={{
          groupLabel: tShared('group_label'),
          allLabel,
          subsetLabel: tShared('subset'),
          selectedLocationsLabel: tShared('selected_locations_label'),
          recommendedPillLabel: tShared('recommended_pill'),
          multiSelect: multiSelectLabels,
          removeAriaLabel: (name: string) =>
            tShared('remove_location_aria_label', { name }),
        }}
      />
    </div>
  )
}

export default LocationsStep
